// allview/Filters.js

import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import "./Filters.css";

const Filters = ({ setFilteredData, setFilters }) => {
  const [allData, setAllData] = useState([]);

  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });

  // ✅ MULTI-SELECT ARRAYS
  const [pid, setPid] = useState([]);
  const [orgName, setOrgName] = useState([]);
  const [managerName, setManagerName] = useState([]);
  const [managerError, setManagerError] = useState("");

  const [filteredDropdownData, setFilteredDropdownData] = useState({
    pid: [],
    orgName: [],
    managerName: [],
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  /* ================= MULTI SELECT STYLE ================= */

  const multiSelectStyles = {
    container: (provided) => ({
      ...provided,
      width: "230px",
    }),
    control: (provided) => ({
      ...provided,
      minHeight: "45px",
      maxHeight: "120px",
      overflowY: "auto",
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      padding: "5px",
    }),
    multiValue: (provided) => ({
      ...provided,
      width: "100%",
      marginBottom: "4px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: "13px",
    }),
    menu: (provided) => ({
      ...provided,
      width: "240px",
    }),
  };

  const toOptions = (arr) =>
    arr.map((val) => ({ label: val, value: val }));

  /* ================= FETCH MASTER DATA ================= */

  useEffect(() => {
    const fetchAllDropdownData = async () => {
      setInitialLoading(true);
      try {
        const response = await axios.get(
          "https://api-usersreport-insights.haqdarshak.com/dropdown-data-all"
        );

        const data = response.data;
        setAllData(data);

        const uniquePIDs = [
          ...new Set(data.map((item) => item.Organization_PID)),
        ].filter(Boolean);

        const uniqueOrgs = [
          ...new Set(data.map((item) => item.Organization_Project_Name)),
        ].filter(Boolean);

        const uniqueManagers = [
          ...new Set(data.map((item) => item.Manager_Name)),
        ].filter(Boolean);

        setFilteredDropdownData({
          pid: uniquePIDs,
          orgName: uniqueOrgs,
          managerName: uniqueManagers,
        });

        // ✅ Restore arrays
        const savedPid = JSON.parse(localStorage.getItem("selectedPid") || "[]");
        const savedOrg = JSON.parse(localStorage.getItem("selectedOrgName") || "[]");
        const savedManager = JSON.parse(localStorage.getItem("selectedManagerName") || "[]");

        setPid(savedPid);
        setOrgName(savedOrg);
        setManagerName(savedManager);

      } catch (error) {
        console.error("Error fetching dropdown master data:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchAllDropdownData();
  }, []);

  /* ================= DEPENDENT FILTER LOGIC ================= */

  useEffect(() => {
    if (!allData.length) return;

    // PID options
    let pidFiltered = allData;

    if (orgName.length) {
      pidFiltered = pidFiltered.filter((item) =>
        orgName.includes(item.Organization_Project_Name)
      );
    }

    if (managerName.length) {
      pidFiltered = pidFiltered.filter((item) =>
        managerName.includes(item.Manager_Name)
      );
    }

    const uniquePIDs = [
      ...new Set(pidFiltered.map((item) => item.Organization_PID)),
    ].filter(Boolean);

    // ORG options
    let orgFiltered = allData;

    if (pid.length) {
      orgFiltered = orgFiltered.filter((item) =>
        pid.includes(item.Organization_PID)
      );
    }

    if (managerName.length) {
      orgFiltered = orgFiltered.filter((item) =>
        managerName.includes(item.Manager_Name)
      );
    }

    const uniqueOrgs = [
      ...new Set(orgFiltered.map((item) => item.Organization_Project_Name)),
    ].filter(Boolean);

    // MANAGER options
    let managerFiltered = allData;

    if (pid.length) {
      managerFiltered = managerFiltered.filter((item) =>
        pid.includes(item.Organization_PID)
      );
    }

    if (orgName.length) {
      managerFiltered = managerFiltered.filter((item) =>
        orgName.includes(item.Organization_Project_Name)
      );
    }

    const uniqueManagers = [
      ...new Set(managerFiltered.map((item) => item.Manager_Name)),
    ].filter(Boolean);

    setFilteredDropdownData({
      pid: uniquePIDs,
      orgName: uniqueOrgs,
      managerName: uniqueManagers,
    });
  }, [pid, orgName, managerName, allData]);

  /* ================= APPLY FILTER ================= */

  const handleFilterSubmit = async () => {
    if (!managerName.length) {
      setManagerError("Please select Manager name filter");
      return;
    } else {
      setManagerError("");
    }

    setLoading(true);

    try {
      localStorage.setItem("selectedPid", JSON.stringify(pid));
      localStorage.setItem("selectedOrgName", JSON.stringify(orgName));
      localStorage.setItem("selectedManagerName", JSON.stringify(managerName));

      setFilters({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        pid,
        orgName,
        managerName,
      });

      const response = await axios.get("https://api-usersreport-insights.haqdarshak.com/users", {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          pid,
          orgName,
          managerName,
        },
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach((key) => {
            if (Array.isArray(params[key])) {
              params[key].forEach((val) => searchParams.append(key, val));
            } else if (params[key]) {
              searchParams.append(key, params[key]);
            }
          });
          return searchParams.toString();
        },
      });

      setFilteredData(response.data);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ================= CLEAR FILTER ================= */

  const handleClearFilters = () => {
    localStorage.removeItem("selectedPid");
    localStorage.removeItem("selectedOrgName");
    localStorage.removeItem("selectedManagerName");

    setDateRange({ startDate: "", endDate: "" });
    setPid([]);
    setOrgName([]);
    setManagerName([]);
    setManagerError("");

    setFilters({});
    setFilteredData([]);
  };

  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  };

  const minDate = "2024-04-01";
  const maxDate = getYesterdayDate();

  if (initialLoading) {
    return (
      <div className="filters-container-v2">
        <div className="loading-indicator">Loading Filters...</div>
      </div>
    );
  }

  return (
    <div className="filters-container-v2">

      {/* DATE RANGE */}
      <div className="filter-group-v2">
        <label>Date Range:</label>
        <div className="date-row">
          <input
            type="date"
            value={dateRange.startDate}
            min={minDate}
            max={maxDate}
            onChange={(e) =>
              setDateRange({ ...dateRange, startDate: e.target.value })
            }
            className="filter-date"
          />
          <span className="date-separator">to</span>
          <input
            type="date"
            value={dateRange.endDate}
            min={minDate}
            max={maxDate}
            onChange={(e) =>
              setDateRange({ ...dateRange, endDate: e.target.value })
            }
            className="filter-date"
          />
        </div>
      </div>

      {/* PID */}
      <div className="filter-group-v2">
        <label>PID:</label>
        <Select
          isMulti
          styles={multiSelectStyles}
          options={toOptions(filteredDropdownData.pid)}
          value={toOptions(pid)}
          onChange={(selected) =>
            setPid(selected ? selected.map((s) => s.value) : [])
          }
        />
      </div>

      {/* ORG */}
      <div className="filter-group-v2">
        <label>Organization/State Name:</label>
        <Select
          isMulti
          styles={multiSelectStyles}
          options={toOptions(filteredDropdownData.orgName)}
          value={toOptions(orgName)}
          onChange={(selected) =>
            setOrgName(selected ? selected.map((s) => s.value) : [])
          }
        />
      </div>

      {/* MANAGER */}
      <div className="filter-group-v2">
        <label>Manager Name:</label>
        <Select
          isMulti
          styles={multiSelectStyles}
          options={toOptions(filteredDropdownData.managerName)}
          value={toOptions(managerName)}
          onChange={(selected) =>
            setManagerName(selected ? selected.map((s) => s.value) : [])
          }
        />

        {managerError && (
          <p style={{ color: "red", fontSize: "13px", marginTop: "4px" }}>
            {managerError}
          </p>
        )}
      </div>

      {/* BUTTONS */}
      <div className="filter-buttons-v2">
        <button
          className="filter-button-v2"
          onClick={handleFilterSubmit}
          disabled={loading}
        >
          {loading ? "Loading..." : "Apply Filters"}
        </button>

        <button
          className="filter-button-v2 clear-button-v2"
          onClick={handleClearFilters}
        >
          Clear Filters
        </button>
      </div>

      {/* NOTES */}
      <p style={{ marginTop: "-10px", fontSize: "14px", color: "gray" }}>
        📌 This data is from 1st April 2025
      </p>
      <p style={{ marginTop: "-10px", fontSize: "14px", color: "gray" }}>
        📌 Individual manager-wise data is roll-up data, so summing up the total managers in a project will not equal the project’s total data.
      </p>

    </div>
  );
};

export default Filters;




// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import "./Filters.css";

// const Filters = ({ setFilteredData, setFilters }) => {
//   const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
//   const [pid, setPid] = useState("");
//   const [orgName, setOrgName] = useState("");
//   const [managerName, setManagerName] = useState("");
//   const [stateName, setStateName] = useState("");

//   const [loading, setLoading] = useState(false);
//   const [loadingFilters, setLoadingFilters] = useState({
//     pid: false,
//     orgName: false,
//     managerName: false,
//     stateName: false,
//   });

//   const [dropdownData, setDropdownData] = useState({
//     pid: [],
//     orgName: [],
//     managerName: [],
//     stateName: [],
//   });

//   const [filteredDropdownData, setFilteredDropdownData] = useState({
//     pid: [],
//     orgName: [],
//     managerName: [],
//     stateName: [],
//   });

//   const debounce = (func, delay) => {
//     let timer;
//     return (...args) => {
//       clearTimeout(timer);
//       timer = setTimeout(() => func(...args), delay);
//     };
//   };

//   useEffect(() => {
//     const fetchDropdownData = async () => {
//       try {
//         const response = await axios.get("http://localhost:3006/dropdown-data");
//         setDropdownData(response.data);
//         setFilteredDropdownData(response.data);
//       } catch (error) {
//         console.error("Error fetching dropdown data:", error);
//       }
//     };
//     fetchDropdownData();
//   }, []);

//   useEffect(() => {
//     const fetchDependentData = async () => {
//       if (!pid && !orgName && !managerName && !stateName) return;

//       setLoadingFilters({
//         pid: Boolean(pid),
//         orgName: Boolean(orgName),
//         managerName: Boolean(managerName),
//         stateName: Boolean(stateName),
//       });

//       try {
//         const response = await axios.get("http://localhost:3006/dropdown-data", {
//           params: { pid, orgName, managerName, stateName },
//         });
//         setFilteredDropdownData(response.data);
//       } catch (error) {
//         console.error("Error fetching dependent dropdown data:", error);
//       } finally {
//         setLoadingFilters({
//           pid: false,
//           orgName: false,
//           managerName: false,
//           stateName: false,
//         });
//       }
//     };

//     const debouncedFetch = debounce(fetchDependentData, 500);
//     debouncedFetch();
//   }, [pid, orgName, managerName, stateName]);

//   const handleFilterSubmit = async () => {
//     setLoading(true);
//     try {
//       setFilters({ startDate: dateRange.startDate, endDate: dateRange.endDate, pid, orgName, managerName, stateName });

//       const response = await axios.get("http://localhost:3006/users", {
//         params: { startDate: dateRange.startDate, endDate: dateRange.endDate, pid, orgName, managerName, stateName },
//       });

//       setFilteredData(response.data);
//     } catch (error) {
//       console.error("Error fetching filtered data:", error);
//       alert("Failed to fetch filtered data. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleClearFilters = () => {
//     setDateRange({ startDate: "", endDate: "" });
//     setPid("");
//     setOrgName("");
//     setManagerName("");
//     setStateName("");
//     setFilteredDropdownData(dropdownData);
//     setFilters({});
//     setFilteredData([]);
//   };

//   const getYesterdayDate = () => {
//     const yesterday = new Date();
//     yesterday.setDate(yesterday.getDate() - 1);
//     return yesterday.toISOString().split("T")[0];
//   };

//   const minDate = "2024-04-01";
//   const maxDate = getYesterdayDate();

//   return (
//     <div className="filters-container-v2">
//       <div className="filter-group-v2">
//         <label>Date Range:</label>
//         <div className="date-range-labels-v2">
//           <label htmlFor="start-date">From:</label>
//           <input
//             id="start-date"
//             type="date"
//             value={dateRange.startDate}
//             onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
//             className="filter-date"
//             min={minDate}
//             max={maxDate}
//           />
//           <input
//             id="end-date"
//             type="date"
//             value={dateRange.endDate}
//             onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
//             className="filter-date"
//             min={minDate}
//             max={maxDate}
//           />
//         </div>
//       </div>

//       <div className="filter-group-v2">
//         <label>PID:</label>
//         <input
//           type="text"
//           value={pid}
//           onChange={(e) => setPid(e.target.value)}
//           placeholder="Enter PID"
//           className="filter-text-v2"
//           list="pid-options"
//           disabled={Object.values(loadingFilters).some((v) => v)}
//         />
//         {loadingFilters.pid && <span>Loading...</span>}
//         <datalist id="pid-options">
//           {filteredDropdownData.pid.map((option, index) => (
//             <option key={index} value={option} />
//           ))}
//         </datalist>
//       </div>

//       <div className="filter-group-v2">
//         <label>Organization Name:</label>
//         <input
//           type="text"
//           value={orgName}
//           onChange={(e) => setOrgName(e.target.value)}
//           placeholder="Enter Organization Name"
//           className="filter-text-v2"
//           list="orgName-options"
//           disabled={Object.values(loadingFilters).some((v) => v)}
//         />
//         {loadingFilters.orgName && <span>Loading...</span>}
//         <datalist id="orgName-options">
//           {filteredDropdownData.orgName.map((option, index) => (
//             <option key={index} value={option} />
//           ))}
//         </datalist>
//       </div>

//       <div className="filter-group-v2">
//         <label>Manager Name:</label>
//         <input
//           type="text"
//           value={managerName}
//           onChange={(e) => setManagerName(e.target.value)}
//           placeholder="Enter Manager Name"
//           className="filter-text-v2"
//           list="managerName-options"
//           disabled={Object.values(loadingFilters).some((v) => v)}
//         />
//         {loadingFilters.managerName && <span>Loading...</span>}
//         <datalist id="managerName-options">
//           {filteredDropdownData.managerName.map((option, index) => (
//             <option key={index} value={option} />
//           ))}
//         </datalist>
//       </div>

//       <div className="filter-group-v2">
//         <label>State:</label>
//         <input
//           type="text"
//           value={stateName}
//           onChange={(e) => setStateName(e.target.value)}
//           placeholder="Enter State"
//           className="filter-text-v2"
//           list="stateName-options"
//           disabled={Object.values(loadingFilters).some((v) => v)}
//         />
//         {loadingFilters.stateName && <span>Loading...</span>}
//         <datalist id="stateName-options">
//           {filteredDropdownData.stateName.map((option, index) => (
//             <option key={index} value={option} />
//           ))}
//         </datalist>
//       </div>

//       <div className="filter-buttons-v2">
//         <button className="filter-button-v2" onClick={handleFilterSubmit} disabled={loading || Object.values(loadingFilters).some((val) => val)}>
//           {loading ? "Loading..." : "Apply Filters"}
//         </button>
//         <button className="filter-button-v2 clear-button-v2" onClick={handleClearFilters} disabled={loading}>
//           Clear Filters
//         </button>
//       </div>
//     </div>
//   );
// };

// export default Filters;
